import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Order, OrderEvents } from '@app/services/order/order.model';
import { OrderService } from '@app/services/order/order.service';
import { OrderStatus } from '@pdqdev/pdq-shared/dist/entities';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { ToastService } from '@storybook/services/toast/toast.service';
import { first } from 'rxjs/operators';
import { TimeUnits } from 'src/utils';
import { FulfillService } from '../../fulfill.service';
import { OrderUpdateCase, PanelState } from '../../utils/models';

enum UPDATE_ACTION_STATE {
  SYNC = 'sync',
  APPROVE = 'approve',
}

const SYNC_ACTION_STATUSES = [
  OrderUpdateCase.LINE_ITEMS_CHANGED,
  OrderUpdateCase.SHIPPING_INFO,
  OrderUpdateCase.LINE_ITEMS_LOCATION_CHANGED,
];

const APPROVE_ACTION_STATUSES = [
  OrderUpdateCase.FINANCIAL_STATUS_ISSUE,
  OrderUpdateCase.FINANCIAL_STATUS_PAID,
  OrderUpdateCase.FULFILLABLE_QUANTITY,
  OrderUpdateCase.VARIANT,
  OrderUpdateCase.SKU,
];

@Component({
  selector: 'fulfill-side-panel-update-banner',
  templateUrl: './fulfill-side-panel-update-banner.component.html',
  styleUrls: ['./fulfill-side-panel-update-banner.component.scss'],
  standalone: false,
})
export class FulfillSidePanelUpdateBannerComponent implements OnInit, OnChanges {
  @Input() batch;
  @Input() order;
  readonly OrderUpdateCase = OrderUpdateCase;
  readonly UpdateAction = UPDATE_ACTION_STATE;
  readonly OrderEvents = OrderEvents;
  updateAction: UPDATE_ACTION_STATE;

  resolveConflictLoading = false;
  hideBanner: boolean = false;
  displayBannerInfo: boolean = false;
  loading = false;
  PanelState = PanelState;

  constructor(
    private fulfillService: FulfillService,
    private host: ElementRef<HTMLElement>,
    private orderService: OrderService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.initUpdateActionState();
      this.displayBannerInfo = this.fulfillService.panelState === PanelState.ORDER;
    }
  }

  initUpdateActionState() {
    const updateTypes = [...(this.order?.updateType ?? []), ...(this.order?.updateTypeV2 ?? [])];
    const isSyncAction = updateTypes.some((status) => SYNC_ACTION_STATUSES.includes(status));

    if (isSyncAction) {
      this.updateAction = UPDATE_ACTION_STATE.SYNC;
    } else {
      this.updateAction = UPDATE_ACTION_STATE.APPROVE;
    }
  }

  onDismiss(event, orderEvent: OrderEvents) {
    if (!event) {
      return;
    }

    this.fulfillService.resetUpdateType(this.order.id, orderEvent);
    this.host.nativeElement.remove();
    const activeBatch = this.fulfillService.getActiveBatch;
    const updatedBatch = { ...activeBatch };
    updatedBatch.orders = updatedBatch.orders.map((o) => {
      if (o.id === this.order.id) {
        o.updateType = [];
        o.updateTypeV2 = [];
      }
      return o;
    });
    if (this.batchWarningsEmpty(updatedBatch.orders)) {
      this.fulfillService.emitRefreshBatchInList({ batchId: updatedBatch.id });
    }
    this.fulfillService.setActiveBatch(updatedBatch);
  }

  batchWarningsEmpty(batchOrders: Order[]): boolean {
    return batchOrders.every((o) => (o.updateType?.length ?? 0) + (o.updateTypeV2?.length ?? 0) === 0);
  }

  async onSyncSubmit(event) {
    if (!event) {
      return;
    }

    const batchId = this.fulfillService?.activeBatch?.id ?? this.batch?.id;
    this.loading = true;
    await this.fulfillService.backToAssign(this.order, batchId, true, true);

    this.fulfillService.onVoidLabels.pipe(first()).subscribe((res) => {
      if (res) {
        this.orderService.syncOrder(this.order.id).then((order) => {
          this.loading = false;
          this.handleToastAction(order[0]);
          this.fulfillService.setPanelState(PanelState.BATCH);
          this.fulfillService.emitRefreshBatchInList({ batchId });
        });
      }
    });
  }

  get hasPendingUpdates() {
    const pendingUpdates =
      this.fulfillService.panelState === PanelState.ORDER
        ? this.order.pendingUpdates
        : this.batch.orders.flatMap((order) => order.pendingUpdates || []);
    return pendingUpdates?.some((update) => !update.dismissedBy);
  }

  handleOrdersUpdated(updatedOrders: { id: string; pendingUpdates: any[] }[]) {
    if (this.fulfillService.panelState === PanelState.ORDER) {
      const pendingUpdates = updatedOrders.find((o) => o.id === this.order.id)?.pendingUpdates ?? [];

      this.order.pendingUpdates = pendingUpdates;
      this.fulfillService.updateOrderInBatch(this.batch.id, this.order);
      this.fulfillService.updateOrderInActiveBatch(this.order, this.fulfillService.activeBatch.id);
    } else {
      this.batch.orders.forEach((order) => {
        const updated = updatedOrders.find((o) => o.id === order.id);
        if (updated) {
          order.pendingUpdates = updated.pendingUpdates;
        }
        this.fulfillService.updateOrderInBatch(this.batch.id, order);
        this.fulfillService.updateOrderInActiveBatch(order, this.fulfillService.activeBatch.id);
      });
    }

    this.fulfillService.emitRefreshBatchInList({ batchId: this.batch.id });
  }

  handleToastAction(order) {
    const orderHasStep = order.orderStatus.includes(OrderStatus.STEP_NEW)
      ? 'Verify'
      : order.orderStatus.includes(OrderStatus.STEP_ASSIGN)
        ? 'Assign'
        : null;
    if (orderHasStep) {
      this.toastService.show(PdqToastComponent, {
        message: 'Order synched & moved back to ' + orderHasStep,
        mode: 'info',
        duration: TimeUnits._SECOND_IN_MS * 3,
      });
    }
  }
}
